import type { KeyboardEvent } from 'react'

export const handleIntegerKeyDown = (
  e: KeyboardEvent<HTMLInputElement>,
  maxInputLength: number = 16
) => {
  const { key, ctrlKey, metaKey } = e
  const isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)

  if (
    key === 'Backspace' ||
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'Tab' ||
    key === 'Delete' ||
    (((isMac && metaKey) || (!isMac && ctrlKey)) &&
      (key === 'a' || key === 'c' || key === 'v' || key === 'x'))
  ) {
    return
  }
  if (
    !/^(\d*\.?\d{0,2}|\b)$/.test(e.key) ||
    e.currentTarget.value.length >= maxInputLength
  ) {
    e.preventDefault()
  }
  if (e.key === '.' || e.key === 'e' || e.key === '-') {
    // Prevent the entry of '.', 'e', and '-' in the input
    e.preventDefault()
  }
}

export const handleDecimalKeyDown = (
  e: KeyboardEvent<HTMLInputElement>,
  maxInputLength: number = 16,
  maxDecimalPlaces: number = 2
) => {
  const { key, ctrlKey, metaKey } = e
  const isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)
  const currentValue = e.currentTarget.value

  // Allow control keys
  if (
    key === 'Backspace' ||
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'Tab' ||
    key === 'Delete' ||
    key === 'Home' ||
    key === 'End' ||
    (((isMac && metaKey) || (!isMac && ctrlKey)) &&
      (key === 'a' || key === 'c' || key === 'v' || key === 'x'))
  ) {
    return
  }

  // Prevent non-numeric keys except decimal point
  if (!/^\d$/.test(key) && key !== '.') {
    e.preventDefault()
    return
  }

  // Handle decimal point
  if (key === '.') {
    // Prevent multiple decimal points
    if (currentValue.includes('.')) {
      e.preventDefault()
      return
    }
    // Allow decimal point if current value is empty or contains only digits
    if (currentValue === '' || /^\d+$/.test(currentValue)) {
      return
    }
    e.preventDefault()
    return
  }

  // Handle digits
  if (/^\d$/.test(key)) {
    // Check max length
    if (currentValue.length >= maxInputLength) {
      e.preventDefault()
      return
    }

    // Check decimal places limit
    const decimalIndex = currentValue.indexOf('.')
    if (decimalIndex !== -1) {
      const decimalPlaces = currentValue.length - decimalIndex - 1
      const cursorPosition = e.currentTarget.selectionStart || 0

      // If cursor is after decimal point and we're at max decimal places
      if (cursorPosition > decimalIndex && decimalPlaces >= maxDecimalPlaces) {
        e.preventDefault()
        return
      }
    }

    return
  }

  // Block everything else
  e.preventDefault()
}

export const handleDecimalInput = (
  e: React.FormEvent<HTMLInputElement>,
  maxInputLength: number = 16,
  maxDecimalPlaces: number = 2
) => {
  let { value } = e.currentTarget

  // Remove any non-numeric characters except decimal point
  value = value.replaceAll(/[^0-9.]/g, '')

  // Handle multiple decimal points
  const decimalCount = (value.match(/\./g) || []).length
  if (decimalCount > 1) {
    const firstDecimalIndex = value.indexOf('.')
    value =
      value.substring(0, firstDecimalIndex + 1) +
      value.substring(firstDecimalIndex + 1).replaceAll('.', '')
  }

  // Limit decimal places
  const decimalIndex = value.indexOf('.')
  if (
    decimalIndex !== -1 &&
    value.length - decimalIndex - 1 > maxDecimalPlaces
  ) {
    value = value.substring(0, decimalIndex + maxDecimalPlaces + 1)
  }

  // Limit total length
  if (value.length > maxInputLength) {
    value = value.substring(0, maxInputLength)
  }

  // Update the input value
  e.currentTarget.value = value
  return value
}

export const handleIntegerInput = (
  e: React.FormEvent<HTMLInputElement>,
  maxInputLength: number = 16
) => {
  const { value } = e.currentTarget

  if (value.length > maxInputLength) {
    const truncatedValue = value.substring(0, maxInputLength)
    e.currentTarget.value = truncatedValue
    return e.currentTarget.value
  }
}

export const formatPriceWithCommas = (value: string | number): string => {
  if (!value && value !== 0) return ''

  const stringValue = String(value)
  const parts = stringValue.split('.')

  const integerPart = parts[0]
    .split('')
    .reverse()
    .reduce((acc, digit, index) => {
      return index > 0 && index % 3 === 0 ? `${digit},${acc}` : `${digit}${acc}`
    }, '')

  return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart
}

export const removePriceCommas = (value: string): string => {
  return value.replaceAll(/,/g, '')
}

export const handlePriceInput = (
  e: React.ChangeEvent<HTMLInputElement>,
  onChange: (value: string) => void,
  maxInputLength: number = 16,
  maxDecimalPlaces: number = 2
) => {
  const input = e.currentTarget
  let value = input.value

  value = removePriceCommas(value)

  value = value.replaceAll(/[^0-9.]/g, '')

  const decimalCount = (value.match(/\./g) || []).length
  if (decimalCount > 1) {
    const firstDecimalIndex = value.indexOf('.')
    value =
      value.substring(0, firstDecimalIndex + 1) +
      value.substring(firstDecimalIndex + 1).replaceAll(/\./g, '')
  }

  const decimalIndex = value.indexOf('.')
  if (
    decimalIndex !== -1 &&
    value.length - decimalIndex - 1 > maxDecimalPlaces
  ) {
    value = value.substring(0, decimalIndex + maxDecimalPlaces + 1)
  }

  if (value.length > maxInputLength) {
    value = value.substring(0, maxInputLength)
  }

  const formattedValue = formatPriceWithCommas(value)

  input.value = formattedValue

  const endPosition = formattedValue.length
  requestAnimationFrame(() => {
    input.setSelectionRange(endPosition, endPosition)
  })

  onChange(value)
}

export const handlePriceKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
  const { key, ctrlKey, metaKey } = e
  const isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)
  const input = e.currentTarget
  const cursorPosition = input.selectionStart || 0
  const valueLength = input.value.length

  if (
    key === 'Backspace' ||
    key === 'Delete' ||
    key === 'Tab' ||
    (((isMac && metaKey) || (!isMac && ctrlKey)) &&
      (key === 'a' || key === 'c' || key === 'v' || key === 'x'))
  ) {
    return
  }

  if (
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'Home' ||
    key === 'End'
  ) {
    e.preventDefault()
    requestAnimationFrame(() => {
      input.setSelectionRange(valueLength, valueLength)
    })
    return
  }

  if (!/^\d$/.test(key) && key !== '.') {
    e.preventDefault()
    return
  }

  if (cursorPosition !== valueLength) {
    requestAnimationFrame(() => {
      input.setSelectionRange(valueLength, valueLength)
    })
  }
}

export const handlePriceClick = (
  e: React.MouseEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>
) => {
  const input = e.currentTarget
  const valueLength = input.value.length

  requestAnimationFrame(() => {
    input.setSelectionRange(valueLength, valueLength)
  })
}
